import { html, render, RootPart, TemplateResult, PropertyValues } from 'lit';
import { property, customElement, query, state } from 'lit/decorators.js';
import { GridColumnElement } from '@vaadin/vaadin-grid/vaadin-grid-column';
import { GridElement, GridItemModel } from '@vaadin/vaadin-grid/vaadin-grid';
import { registerStyles, unsafeCSS } from '@vaadin/vaadin-themable-mixin/register-styles';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { styles } from '../view/styles';
import { WEB_COMPONENT_GZ_DATA_TABLE } from '../../../webcomponents.constants';
import {
  DataTableDisplayType,
  DataTableLoaderLayout,
  DataTableStrings,
  IColumnType,
  IDataTableColumn,
  ProtectedDataTableWebComponent,
} from '../types';
import { tableFooter, rowDeletion, rowEditing, rowReordering, rowSelection } from '../decorators';
import { styles as vaadinGridStyles } from '../view/shared-vaadin-grid.scss';
import { DATA_TABLE_DEFAULT_LOADER_LAYOUT } from '../types/constants';
import { FormFieldInput } from '../../gzForm/formField/component/FormFieldInput';

registerStyles('vaadin-grid', unsafeCSS(`${vaadinGridStyles}`), { moduleId: 'data-table-styles' });

/**
 * @summary Lightweight data table web component, with delete, edit, selection and reorder functionality.
 * Built using vaadin-grid: https://cdn.vaadin.com/vaadin-grid/5.8.4/#/namespaces/Vaadin.Grid
 * @tag gz-data-table
 * @fires rows-reordered - Custom event dispatched when the rows are reordered
 * @fires rows-reset-order - Custom event dispatched when the order of rows is reset
 * @fires selection-changed - Custom event dispatched when the selection changes
 * @fires row-deleted - Custom event dispatched when a row is deleted
 * @fires row-edited - Custom event dispatched when a row is edited
 * @fires empty-state-footer-button-clicked - Custom event dispatched when the empty state footer button is clicked
 */
@customElement(WEB_COMPONENT_GZ_DATA_TABLE)
@rowDeletion
@rowEditing
@rowReordering
@rowSelection
@tableFooter
export class GzDataTableWebComponent<TDataTableItem = unknown> extends FormFieldInput {
  /**
   * The array of objects to be displayed on the table, row by row
   */
  @property({ type: Array, attribute: true, reflect: true })
  items: TDataTableItem[] = [];

  /**
   * Describes the columns of the table
   */
  @property({ type: Array, attribute: true, reflect: true })
  columns: IDataTableColumn[] = [];

  /**
   * If set, the table is disabled and the user cannot interact with it
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  disabled = false;

  /**
   * Defines the display type of the table, regarding the width of all the columns which do not have a fixed width
   * set via the <code>IDataTableColumn</code> interface
   */
  @property({ type: String, attribute: true, reflect: true })
  displayType = DataTableDisplayType.FLEX;

  /**
   * Displays the loading state of the table if set to true
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  loading = false;

  /**
   * Defines the number of columns and rows of the loader displayed when the table is loading
   */
  @property({ type: Object, attribute: true, reflect: true })
  loaderLayout: DataTableLoaderLayout = DATA_TABLE_DEFAULT_LOADER_LAYOUT;

  /**
   * The strings used in this component in tooltips, buttons, etc. They need to be translated using translateFn!
   * The exported function <code>getDataTableDefaultStrings()</code> can be used to get the default strings
   */
  @property({ type: Object, attribute: true, reflect: true })
  strings: DataTableStrings;

  @query('vaadin-grid')
  protected grid: GridElement;

  @state()
  protected currentItems: TDataTableItem[];

  @state()
  private gridColumnElements: { [columnKey: string]: GridColumnElement } = {};

  private readonly defaultMinColumnWidth = 80; // px

  static get styles() {
    return styles;
  }

  updated(changedProperties: PropertyValues): void {
    if (this.displayType === DataTableDisplayType.AUTO_WIDTH) {
      setTimeout(() => {
        // vaadin-grid needs to re-render the table and compute its size based on the content width
        // This behaviour may cause the table to flicker on every rerender, because the width is computed only after rendering
        // https://cdn.vaadin.com/vaadin-grid/5.8.4/#/elements/vaadin-grid-column#property-autoWidth
        this.grid.recalculateColumnWidths();
      }, 0);
    }
    if (changedProperties.has('items')) {
      this.currentItems = [...this.items];
    }
  }

  render() {
    if (this.loading) {
      return this.renderLoader();
    }
    this.checkStrings();
    return html`
      <div class="disable-interaction"></div>
      <vaadin-grid
        for-gz-data-table
        .items="${this.currentItems}"
        .cellClassNameGenerator="${this.cellClassNameGenerator.bind(this)}"
        ?disabled="${this.disabled}"
        height-by-rows
        theme="compact no-border"
        @column-resize="${this.onColumnResize}"
      >
        ${this.renderColumns()}
      </vaadin-grid>
    `;
  }

  addItem(item: TDataTableItem): void {
    this.currentItems = [...this.currentItems, item];
  }

  addItemToIndex(item: TDataTableItem, index: number): void {
    this.currentItems.splice(index, 0, item);
    this.currentItems = [...this.currentItems];
  }

  getFormValue(): TDataTableItem[] {
    return this.currentItems;
  }

  setFormValue(items: TDataTableItem[]) {
    this.currentItems = items;
  }

  protected get instance(): ProtectedDataTableWebComponent {
    // this getter should be used to access the decorators' protected methods and attributes across other decorators
    return this as unknown as ProtectedDataTableWebComponent;
  }

  protected renderColumn(column: IDataTableColumn, index: number): GridColumnElement {
    const columnElement =
      this.gridColumnElements[column.key] ?? (document.createElement('vaadin-grid-column') as GridColumnElement);
    this.gridColumnElements = { ...this.gridColumnElements, [column.key]: columnElement };
    columnElement.resizable = true;
    columnElement.path = column.key;
    columnElement.header = column.header;

    if (column.width) {
      columnElement.width = column.width;
    }
    if (column.frozen) {
      columnElement.frozen = true;
      columnElement.resizable = false;
    }
    if (!isEmpty(this.currentItems) && this.displayType === DataTableDisplayType.AUTO_WIDTH) {
      columnElement.flexGrow = 0;
      columnElement.autoWidth = true;
    }

    columnElement.headerRenderer = root => this.renderColumnHeader(root, column);
    columnElement.renderer = (root, _, rowData) => this.renderColumnCell(root, rowData, column);
    columnElement.footerRenderer = this.renderEmptyFooter;
    if (column.footerRenderer) {
      columnElement.footerRenderer = root => render(column.footerRenderer?.(column), root);
    }
    return columnElement;
  }

  protected renderColumnCell(
    root: HTMLElement,
    rowData: GridItemModel | undefined,
    column: IDataTableColumn
  ): RootPart | undefined {
    if (!rowData || !rowData.item) {
      return;
    }
    const content = rowData.item[column.key];

    if (column.renderer) {
      return render(column.renderer?.(content, column, rowData.index, false, rowData.item), root);
    }

    return render(this.renderColumnCellElements(content, column, rowData.index), root);
  }

  protected renderColumnCellElements(content: unknown, column: IDataTableColumn, rowIndex?: number): TemplateResult[] {
    // the unused parameters of renderColumnCellElements are actually used by the functions that override it
    if (isEmpty(content) && column.columnType === IColumnType.DISPLAY) {
      return [];
    }
    const formattedContent = this.formatContent(content, column);
    const tooltipContent = html`
      <gz-tooltip isEllipsis tooltipText="${formattedContent}" class="cell-label">${formattedContent}</gz-tooltip>
    `;

    return [
      column.copyable
        ? html`<gz-copy-to-clipboard
            confirmationMessage="${this.strings.copyConfirmationTooltip}"
            showIconOnHover
            ?disabled="${this.disabled}"
          >
            <div class="copy-cell-content">${tooltipContent}</div>
          </gz-copy-to-clipboard>`
        : tooltipContent,
    ];
  }

  protected cellClassNameGenerator(columnElement: GridColumnElement, model: GridItemModel): string {
    // The function is used by vaadin-grid to generate class names for cells. It can be overridden by the decorators.
    return '';
  }

  private renderColumns(): GridColumnElement[] | undefined {
    if (isEmpty(this.columns)) {
      return;
    }
    const columnsElements = this.columns.map((column, index) => this.renderColumn(column, index));
    this.updateFooterVisibility();
    return columnsElements;
  }

  private renderColumnHeader(root: HTMLElement, column: IDataTableColumn): RootPart {
    if (column.headerRenderer) {
      return render(column.headerRenderer?.(column), root);
    }
    return render(
      html`
        <gz-tooltip
          isEllipsis
          id="${column.key}-header-tooltip"
          tooltipText="${column.header}"
          class="text-semibold cell-label cell-label--header"
        >
          ${column.header}
        </gz-tooltip>
      `,
      root
    );
  }

  protected renderEmptyFooter(root: HTMLElement): RootPart {
    return render(html``, root);
  }

  private renderLoader(): TemplateResult {
    const loaderRow = html`<gz-loader rows="1" .columns="${this.loaderLayout.columns}"></gz-loader-row>`;
    return html`
      <div class="loading-container">${Array.from({ length: this.loaderLayout.rows }, () => loaderRow)}</div>
    `;
  }

  private updateFooterVisibility(): void {
    // iterate all cached grid column elements and check if they all have the footerRenderer equal to renderEmptyFooter
    const allColumnsHaveEmptyFooters = Object.values(this.gridColumnElements).every(
      columnElement => columnElement.footerRenderer === this.renderEmptyFooter
    );
    if (allColumnsHaveEmptyFooters) {
      // if all columns have empty footers, set their footerRenderer from all columns to undefined,
      // such that __updateHeaderFooterRowVisibility() can work
      Object.keys(this.gridColumnElements).forEach(key => {
        this.gridColumnElements[key].footerRenderer = undefined;
      });
      const footerRow = this.grid?.shadowRoot?.getElementById('footer')?.querySelector('tr');
      // this vaadin-grid method updates the visibility of the footer, setting it to hidden if there is no footerRenderer on any column
      this.grid?.__updateHeaderFooterRowVisibility(footerRow ?? null);
    }
  }

  private formatContent(content: unknown, column: IDataTableColumn): unknown {
    if (isEmpty(content)) {
      return '-';
    }
    switch (column.columnType) {
      case IColumnType.SINGLE_SELECT:
        const displayValue = column.options?.find(item => item.value === content);
        return displayValue ? displayValue.label : content;
      case IColumnType.MULTI_SELECT:
        if (Array.isArray(content)) {
          const labels = content.map(selection => column.options?.find(item => item.value === selection)?.label);
          return labels.join(', ');
        }
        break;
      case IColumnType.NUMBER:
        return column.numberSuffix ? `${content}${column.numberSuffix}` : `${content}`;
    }
    return content;
  }

  private onColumnResize(event: CustomEvent): void {
    const resizedColumn = event.detail.resizedColumn;
    if (!resizedColumn.resizable) {
      return;
    }
    // If the column width set by the user is less than the default min column width, set it to the default.
    // Max column width is handled directly via CSS for all columns and cannot be overridden by the user
    const columnSize = parseInt(resizedColumn.width, 10);
    if (columnSize < this.defaultMinColumnWidth) {
      resizedColumn.width = this.defaultMinColumnWidth + 'px';
    }
  }

  private checkStrings(): void {
    if (isEmpty(this.strings)) {
      throw Error('Strings should be provided for this component.');
    }
  }
}
